
import React, { useState } from "react";
import { Mail, Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

interface QuickEnquiryProps {
  nursingHomeId: string;
  nursingHomeName: string;
  county?: string;
  source?: string;
}

export function QuickEnquiry({
  nursingHomeId,
  nursingHomeName,
  county,
  source = "card",
}: QuickEnquiryProps) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("quick_enquiries").insert({
        email,
        nursing_home_id: nursingHomeId,
        nursing_home_name: nursingHomeName,
        county: county || null,
        source,
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast.success(`Enquiry sent for ${nursingHomeName}`, {
        description: "We'll connect you with this nursing home shortly.",
      });
    } catch (error) {
      console.error("Quick enquiry error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="flex items-center gap-2 text-sm text-grandpal-green font-medium py-2">
        <Check className="h-4 w-4" />
        <span>Enquiry sent — we'll be in touch!</span>
      </div>
    );
  }

  if (!showInput) {
    return (
      <Button
        variant="default"
        className="w-full bg-grandpal-green hover:bg-grandpal-green-dark text-white"
        onClick={(e) => {
          e.stopPropagation();
          setShowInput(true);
        }}
      >
        <Mail className="h-4 w-4 mr-2" />
        Get Details
      </Button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      onClick={(e) => e.stopPropagation()}
      className="flex gap-2"
    >
      <Input
        type="email"
        placeholder="Your email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        className="h-9 text-sm"
        autoFocus
      />
      <Button
        type="submit"
        size="sm"
        disabled={isSubmitting}
        className="bg-grandpal-green hover:bg-grandpal-green-dark text-white shrink-0 h-9"
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          "Send"
        )}
      </Button>
    </form>
  );
}
